import { getMatchStats } from '@/js/api/first2find';
import { defineModule } from '@/js/utils/helpers';

const DURATION = 3000;
const UPDATE_RATE = 60;
const MIN_BAR_HEIGHT = 12;
const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)',
).matches;

const numberFormat = new Intl.NumberFormat('nl-NL');
const dayFormat = new Intl.DateTimeFormat('nl-NL', { weekday: 'short' });
const rangeFormat = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'short',
});

type MatchStats = Awaited<ReturnType<typeof getMatchStats>>;
type Period = 'day' | 'week';

interface Bar {
  label: string;
  matches: number;
}

// Parsed at local midnight, not UTC, or the label lands a day early.
const parseDate = (date: string) => new Date(`${date}T00:00:00`);

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const getPeriod = (block: HTMLElement): Period =>
  block.dataset.period === 'week' ? 'week' : 'day';

const toBars = (stats: MatchStats, period: Period): Bar[] => {
  if (period === 'week') {
    return stats.weeks.map((week) => ({
      label: `${rangeFormat.format(parseDate(week.startDate))} – ${rangeFormat.format(parseDate(week.endDate))}`,
      matches: week.matches,
    }));
  }

  return stats.days.map((day) => ({
    label: capitalize(dayFormat.format(parseDate(day.date))),
    matches: day.matches,
  }));
};

const runCounter = (counter: HTMLElement) => {
  const target = Number(counter.dataset.target);
  if (!Number.isFinite(target)) return;

  const t0 = performance.now();

  const updateCount = () => {
    const ratio = (performance.now() - t0) / DURATION;
    if (ratio < 1) {
      counter.innerText = numberFormat.format(Math.round(ratio * target));
      setTimeout(updateCount, 1000 / UPDATE_RATE);
    } else {
      counter.innerText = numberFormat.format(target);
      counter.style.display = '';
      counter.style.width = '';
      counter.style.textAlign = '';
    }
  };

  updateCount();
};

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      entry.target.classList.add('show');
    });
  },
  { threshold: 0.2 },
);

const counterObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      counterObserver.unobserve(entry.target);
      runCounter(entry.target as HTMLElement);
    });
  },
  { threshold: 0.2 },
);

const applyBars = (block: HTMLElement, bars: Bar[]) => {
  const elements = [...block.querySelectorAll<HTMLElement>('[data-bar]')];
  const labels = [...block.querySelectorAll<HTMLElement>('[data-bar-label]')];

  if (elements.length !== bars.length || labels.length !== bars.length) return;

  const max = Math.max(...bars.map((bar) => bar.matches)) || 1;

  bars.forEach((bar, index) => {
    const element = elements[index];
    const height = Math.max(
      MIN_BAR_HEIGHT,
      Math.round((bar.matches / max) * 100),
    );

    element.style.setProperty('--bar-height', `${height}%`);
    element.style.setProperty('--bar-scale', `${height / 100}`);

    labels[index].innerText = bar.label;

    const badge = element.querySelector<HTMLElement>('.statistics__badge');
    if (badge) badge.innerText = numberFormat.format(bar.matches);
  });
};

const applyCounters = (block: HTMLElement, bars: Bar[]) => {
  const counters = block.querySelectorAll<HTMLElement>('.statistics__counter');
  const { matches } = bars[bars.length - 1];
  const formatted = numberFormat.format(matches);

  counters.forEach((counter) => {
    counter.dataset.target = `${matches}`;

    if (prefersReducedMotion) {
      counter.innerText = formatted;
      return;
    }

    counter.innerText = formatted;
    counter.style.display = 'inline-block';
    counter.style.width = `${counter.offsetWidth}px`;
    counter.style.textAlign = 'center';
    counter.innerText = '0';

    counterObserver.observe(counter);
  });
};

export default defineModule(
  async () => {
    const blocks = [
      ...document.querySelectorAll<HTMLElement>('.content-block--statistics'),
    ];
    if (!blocks.length) return;

    // Collapse the bars before fetching, so they only ever grow towards the
    // figures we end up with instead of animating to the rendered ones first.
    blocks.forEach((block) => block.classList.add('animate'));

    try {
      const stats = await getMatchStats();

      blocks.forEach((block) => {
        const bars = toBars(stats, getPeriod(block));
        if (!bars.length) return;

        applyBars(block, bars);
        applyCounters(block, bars);
      });
    } catch {
      // Without the endpoint the block keeps the figures the editor entered.
    }

    blocks.forEach((block) => observer.observe(block));
  },
  () => {
    observer.disconnect();
    counterObserver.disconnect();
  },
);
